import { DatePickerInput } from "@/components/date-picker-input";
import { StandaloneFilterSelect } from "@/components/ui/filter-select";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { AvailableFilterField } from "@/hooks/use-available-filter-fields";
import { formatDateToISO, safeParseDateString } from "@/lib/date-utils";
import type { StructuredFilter } from "@/lib/filters";
import { cn } from "@/lib/utils";

export interface ValueEditorProps {
  field: AvailableFilterField | undefined;
  filter: StructuredFilter;
  onChange: (values: unknown[]) => void;
  isIncomplete?: boolean;
}

/**
 * Basic implementation covering enum + text input for phase-1.
 * Other data types (number, date, etc.) will be added in later phases.
 */
export function ValueEditor({
  field,
  filter,
  onChange,
  isIncomplete,
}: ValueEditorProps) {
  if (!field) return null;

  // Operators that do not require a value editor
  if (filter.op === "IS_NULL" || filter.op === "IS_NOT_NULL") {
    return null;
  }

  const conditionalClasses = cn(
    "h-9 text-sm",
    isIncomplete &&
      "ring-2 ring-offset-1 ring-offset-background ring-destructive",
  );

  // Enum handling
  if (field.hasEnumValues && Array.isArray(field.enumValues)) {
    const multi = filter.op === "IN" || filter.op === "NOT_IN";

    const selectedVals = Array.isArray(filter.values)
      ? (filter.values as string[])
      : [];

    // Check if enumValues are objects with id/label (SelectOption format) or simple strings
    const isObjectFormat =
      field.enumValues.length > 0 &&
      typeof field.enumValues[0] === "object" &&
      "id" in (field.enumValues[0] as { id: string; label: string });

    if (multi) {
      return (
        <StandaloneFilterSelect
          options={field.enumValues}
          selectedValues={selectedVals}
          onSelectionChange={(vals) => onChange(vals)}
          label="Values"
          maxHeight={200}
          width={200}
          triggerClassName={conditionalClasses}
        />
      );
    }

    // LIKE/ILIKE → free text entry even if enum field
    if (filter.op === "LIKE" || filter.op === "ILIKE") {
      const val = selectedVals.length > 0 ? selectedVals[0] : "";
      return (
        <Input
          value={val}
          placeholder="Contains..."
          className={cn("w-48", conditionalClasses)}
          onChange={(e) => onChange([e.target.value])}
        />
      );
    }

    // single-select using Select component for EQ/NEQ etc.
    const singleVal = selectedVals.length > 0 ? selectedVals[0] : "";

    if (isObjectFormat) {
      // Object format with {id, label}
      const options = field.enumValues as Array<{ id: string; label: string }>;
      return (
        <Select value={singleVal} onValueChange={(v) => onChange([v])}>
          <SelectTrigger className={cn("w-48", conditionalClasses)}>
            <SelectValue placeholder="Select value" />
          </SelectTrigger>
          <SelectContent>
            {options.map((opt) => (
              <SelectItem key={opt.id} value={opt.id}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    // Simple string format
    return (
      <Select value={singleVal} onValueChange={(v) => onChange([v])}>
        <SelectTrigger className={cn("w-48", conditionalClasses)}>
          <SelectValue placeholder="Select value" />
        </SelectTrigger>
        <SelectContent>
          {(field.enumValues as string[]).map((opt) => (
            <SelectItem key={opt} value={opt}>
              {opt}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // Boolean → yes/no picker
  if (field.dataType === "BOOLEAN") {
    const boolVal =
      Array.isArray(filter.values) && filter.values.length > 0
        ? String(filter.values[0])
        : "true";

    return (
      <Select value={boolVal} onValueChange={(v) => onChange([v === "true"])}>
        <SelectTrigger className={cn("w-28", conditionalClasses)}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="true">Yes</SelectItem>
          <SelectItem value="false">No</SelectItem>
        </SelectContent>
      </Select>
    );
  }

  // Number → numeric input
  if (field.dataType === "NUMBER") {
    const numVal =
      Array.isArray(filter.values) && filter.values.length > 0
        ? Number(filter.values[0])
        : undefined;
    return (
      <Input
        type="number"
        value={numVal}
        className={cn("w-32", conditionalClasses)}
        onChange={(e) =>
          onChange([e.target.value === "" ? undefined : Number(e.target.value)])
        }
      />
    );
  }

  // Date / Datetime
  if (field.dataType === "DATE" || field.dataType === "DATETIME") {
    if (filter.op === "BETWEEN") {
      const from =
        Array.isArray(filter.values) && filter.values.length > 0
          ? String(filter.values[0] ?? "")
          : "";
      const to =
        Array.isArray(filter.values) && filter.values.length > 1
          ? String(filter.values[1] ?? "")
          : "";

      return (
        <div
          className={cn(
            "flex gap-2 items-center",
            conditionalClasses.replace("h-9", ""),
          )}
        >
          <DatePickerInput
            value={from}
            onChange={(date) => {
              onChange([formatDateToISO(date) || "", to]);
            }}
            className={cn("w-44", conditionalClasses)}
            maxDate={to ? safeParseDateString(to) : undefined}
          />
          <span className="self-center text-sm">to</span>
          <DatePickerInput
            value={to}
            onChange={(date) => {
              onChange([from, formatDateToISO(date) || ""]);
            }}
            className={cn("w-44", conditionalClasses)}
            minDate={from ? safeParseDateString(from) : undefined}
          />
        </div>
      );
    }

    const dateVal =
      Array.isArray(filter.values) && filter.values.length > 0
        ? String(filter.values[0])
        : "";
    return (
      <DatePickerInput
        value={dateVal}
        onChange={(date) => {
          onChange([formatDateToISO(date) || ""]);
        }}
        className={cn("w-48", conditionalClasses)}
      />
    );
  }

  // Fallback simple text input
  const value =
    Array.isArray(filter.values) && filter.values.length > 0
      ? String(filter.values[0])
      : "";

  return (
    <Input
      value={value}
      placeholder="Value"
      className={cn("w-48", conditionalClasses)}
      onChange={(e) => onChange([e.target.value])}
    />
  );
}
